import {
  ActionButton,
  Detail,
  formatCodeName,
  formatDate,
  formatDateTimeShort,
  InputDataActiveInfoParams,
  InputDataActiveInputType,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  SystemClassifierItemDto,
  Table,
  TextEllipsis,
  useInputDataActiveInfoQuery,
  useSystemClassifierItemById,
  useSystemClassifierItemsBy,
} from '@atlas-frontend/sdk';
import { Flex, Tag } from 'antd';
import { FC, memo, useMemo } from 'react';
import translations from './InputDataActiveInfoModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import styled from 'styled-components';
import {
  colorDraftBg,
  colorDraftBorder,
  colorSuccessBg,
  colorSuccessBorder,
} from '@atlas-frontend/design-tokens';

const { addTranslations, t } = i18nNsFactory('input-data-active-inputs-info');
addTranslations(translations);

export interface InputDataActiveInfoModalProps {
  params: InputDataActiveInfoParams;
  onClose: () => void;
}

const columnHelper = createColumnHelper<InputDataActiveInputType>();

export const InputDataActiveInfoModal: FC<InputDataActiveInfoModalProps> = (
  props,
) => {
  const { params, onClose } = props;
  const { data, isLoading } = useInputDataActiveInfoQuery(params);
  const frequency = useSystemClassifierItemById('FREQUENCY', data?.frequencyId);
  const systemClassifierItemsById = useSystemClassifierItemsBy([
    'DATA_OBJECT_VER_STATUS',
  ]);

  const columns = useMemo(
    () => [
      columnHelper.accessor((rowData) => formatCodeName(rowData), {
        id: 'name',
        header: t('column.name'),
        enableColumnFilter: false,
        meta: { minWidth: '16ch' },
      }),
      columnHelper.accessor(
        (rowData) => formatDateTimeShort(rowData.latestVersionCreatedAt),
        {
          id: 'latestVersionCreatedAt',
          header: t('column.latestVersionCreatedAt'),
          enableColumnFilter: false,
          enableSorting: false,
          meta: { width: '24ch' },
        },
      ),
      columnHelper.accessor('latestVersionStatusId', {
        header: t('column.latestVersionStatusId'),
        cell: (props) => (
          <StatusCell
            statuses={systemClassifierItemsById.DATA_OBJECT_VER_STATUS}
            {...props}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
        meta: { width: '16ch' },
      }),
    ],
    [systemClassifierItemsById.DATA_OBJECT_VER_STATUS],
  );

  if (!data) return null;

  return (
    <Modal
      onCancel={onClose}
      width="40rem"
      loading={isLoading}
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton layoutType="detail" onClick={onClose}>
              {t('cancel')}
            </ActionButton>
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>
            {t('title', { count: data.inputs.length })}
          </TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <Padding scrollable>
        <Flex vertical gap="small">
          <Detail label={t('group')}>
            {`${data?.groupCode} - ${data.groupName}`}
          </Detail>
          <Detail label={t('valueDate')}>{formatDate(data.valueDate)}</Detail>
          <Detail label={t('frequency')}>{frequency?.name}</Detail>
        </Flex>
        <Padding top>
          <Table
            name="input-data-active-info-table"
            columns={columns}
            data={data.inputs}
            defaultSort={[{ id: 'name', desc: false }]}
            persistColumnStyle={false}
          />
        </Padding>
      </Padding>
    </Modal>
  );
};

const StatusCell: FC<
  CellContext<InputDataActiveInputType, any> & {
    statuses: Map<string | number | boolean, SystemClassifierItemDto>;
  }
> = memo((props) => {
  const { statuses } = props;
  const statusId = props.row.original.latestVersionStatusId;
  if (!statusId) return null;

  const status = statuses.get(statusId)?.code;
  if (!status) return null;

  return (
    <StyledTag $isDraft={status === 'DRAFT'}>{t('status.' + status)}</StyledTag>
  );
});

const StyledTag = styled(Tag)<{ $isDraft: boolean }>`
  margin: 0;
  text-transform: uppercase;
  border-color: ${({ $isDraft }) =>
    $isDraft ? colorDraftBorder : colorSuccessBorder};
  background-color: ${({ $isDraft }) =>
    $isDraft ? colorDraftBg : colorSuccessBg};
`;
